import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Spin, Rate, Input, message, Empty, Alert } from 'antd';
import { ArrowLeftOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { Button } from '../../../design-system';
import clientAPI from '../../../service/ApiServiceClientPortal';

const BRAND_BLACK = '#1B1B1B';
const BRAND_YELLOW = '#FED230';
const DEBOUNCE_MS = 500;

const styles = {
    backRow: {
        marginBottom: 16,
    },
    title: {
        fontSize: 22,
        fontWeight: 600,
        color: BRAND_BLACK,
        margin: '0 0 4px 0',
    },
    subtitle: {
        color: '#888',
        fontSize: 14,
        marginBottom: 24,
    },
    card: {
        background: '#fff',
        borderRadius: 8,
        padding: 24,
        marginBottom: 16,
    },
    questionLabel: {
        fontSize: 15,
        fontWeight: 500,
        color: BRAND_BLACK,
        marginBottom: 8,
    },
    savedIndicator: {
        fontSize: 12,
        color: '#52c41a',
        marginTop: 4,
    },
    submitRow: {
        marginTop: 24,
        display: 'flex',
        gap: 12,
    },
};

const ClientFeedbackForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [feedbackItems, setFeedbackItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [concluded, setConcluded] = useState(false);
    const [savedMap, setSavedMap] = useState({});
    const debounceTimers = useRef({});

    const fetchFeedback = useCallback(async () => {
        setLoading(true);
        try {
            const response = await clientAPI.get(`/client-portal/projects/${id}/feedback`);
            const data = response.data?.data || response.data;
            const items = data.feedbackItems || data.items || data || [];
            setFeedbackItems(Array.isArray(items) ? items : []);
            setConcluded(data.concluded || false);
        } catch (err) {
            message.error('Failed to load feedback form');
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        fetchFeedback();
    }, [fetchFeedback]);

    // Cleanup debounce timers on unmount
    useEffect(() => {
        const timers = debounceTimers.current;
        return () => {
            Object.values(timers).forEach(clearTimeout);
        };
    }, []);

    const saveFeedbackItem = async (feedbackId, value) => {
        try {
            await clientAPI.put(`/client-portal/projects/${id}/feedback/${feedbackId}`, { value });
            setSavedMap((prev) => ({ ...prev, [feedbackId]: true }));
            // Clear saved indicator after 2 seconds
            setTimeout(() => {
                setSavedMap((prev) => ({ ...prev, [feedbackId]: false }));
            }, 2000);
        } catch (err) {
            message.error('Failed to save response');
        }
    };

    const handleChange = (feedbackId, value) => {
        // Update local state immediately
        setFeedbackItems((prev) =>
            prev.map((item) =>
                item.id === feedbackId ? { ...item, value } : item
            )
        );

        // Debounce the API call
        if (debounceTimers.current[feedbackId]) {
            clearTimeout(debounceTimers.current[feedbackId]);
        }
        debounceTimers.current[feedbackId] = setTimeout(() => {
            saveFeedbackItem(feedbackId, value);
        }, DEBOUNCE_MS);
    };

    const handleRatingChange = (feedbackId, rating) => {
        // Update local state immediately
        setFeedbackItems((prev) =>
            prev.map((item) =>
                item.id === feedbackId ? { ...item, value: String(rating) } : item
            )
        );
        // Save immediately for ratings (no debounce needed)
        saveFeedbackItem(feedbackId, String(rating));
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            await clientAPI.post(`/client-portal/projects/${id}/feedback/conclude`);
            message.success('Feedback submitted successfully');
            setConcluded(true);
        } catch (err) {
            const errMsg = err.response?.data?.data?.message || err.response?.data?.message || 'Failed to submit feedback';
            message.error(errMsg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: 80 }}>
                <Spin size="large" />
            </div>
        );
    }

    if (feedbackItems.length === 0) {
        return (
            <div>
                <div style={styles.backRow}>
                    <Button
                        variant="secondary"
                        icon={<ArrowLeftOutlined />}
                        onClick={() => navigate(`/client-section/project/${id}`)}
                    >
                        Back to Project
                    </Button>
                </div>
                <Empty description="No feedback items available for this project" />
            </div>
        );
    }

    return (
        <div>
            <div style={styles.backRow}>
                <Button
                    variant="secondary"
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate(`/client-section/project/${id}`)}
                >
                    Back to Project
                </Button>
            </div>

            <h1 style={styles.title}>Project Feedback</h1>
            <p style={styles.subtitle}>
                Your responses are saved automatically as you fill them in.
            </p>

            {concluded && (
                <Alert
                    message="Feedback already submitted"
                    description="You have already submitted your feedback for this project. Thank you!"
                    type="success"
                    showIcon
                    style={{ marginBottom: 24 }}
                />
            )}

            {feedbackItems.map((item) => (
                <div key={item.id} style={styles.card}>
                    <div style={styles.questionLabel}>
                        {item.question || item.label || 'Question'}
                    </div>

                    {item.type === 'rating' ? (
                        <>
                            <Rate
                                value={Number(item.value) || 0}
                                onChange={(val) => handleRatingChange(item.id, val)}
                                disabled={concluded}
                                style={{ color: BRAND_YELLOW }}
                            />
                            {savedMap[item.id] && (
                                <div style={styles.savedIndicator}>
                                    <CheckCircleOutlined /> Saved
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <Input.TextArea
                                rows={3}
                                placeholder="Enter your feedback..."
                                value={item.value || ''}
                                onChange={(e) => handleChange(item.id, e.target.value)}
                                disabled={concluded}
                            />
                            {savedMap[item.id] && (
                                <div style={styles.savedIndicator}>
                                    <CheckCircleOutlined /> Saved
                                </div>
                            )}
                        </>
                    )}
                </div>
            ))}

            {!concluded && (
                <div style={styles.submitRow}>
                    <Button
                        variant="primary"
                        onClick={handleSubmit}
                        loading={submitting}
                    >
                        Submit Feedback
                    </Button>
                </div>
            )}
        </div>
    );
};

export default ClientFeedbackForm;
